import {Amplify} from '@aws-amplify/core'
import {getAuthenticationInstance} from './Authentication/authenticationInstance'
import snackHandler from './Snack/SnackHandler'
import {messages} from './texts'
import {runWhile} from '@oc/utils'

/**
 * Class handling connection, subscribing and automatic reconnection to OC Websocket API.
 * One instance supports only one subscription to keep the onMessageCallback simple.
 */
export class OcWebsocket<T> {
    private ws?: WebSocket
    private resolveConnected?: () => void
    private pingIntervalId?: NodeJS.Timeout
    private connectPromise?: Promise<void>
    /** For edge case when disconnect is requested while still connecting */
    private disconnecting = false

    /**
     * Connects to OC Websocket API and subscribes to specified resource/customer.
     * @param resource Resource name to subscribe to.
     * @param customer (Optional) customer to subscribe to. All customers allowed by OC permissions will be subscribed if this is omitted.
     * @param onMessageCallback Message callback.
     */
    constructor(
        private resource: string,
        private customer: string | undefined,
        private onMessageCallback: (message: T) => void
    ) {
        this.onOpen = this.onOpen.bind(this)
        this.onClose = this.onClose.bind(this)
        this.onMessage = this.onMessage.bind(this)

        this.connect = this.connect.bind(this)
        this.disconnect = this.disconnect.bind(this)
    }

    /**
     * Created connection to OC Websocket API and subscribes to resource specified in constructor.
     * After connection is established periodic ping is made every one minute to keep alive the connection.
     * If the connection is closed for some reason the reconnect is automatically attempted (only once).
     */
    public async connect(): Promise<void> {
        if (this.ws) {
            switch (this.ws.readyState) {
                case WebSocket.OPEN:
                    return
                case WebSocket.CONNECTING:
                    if (this.connectPromise) {
                        return this.connectPromise
                    }
                    break
                case WebSocket.CLOSING:
                case WebSocket.CLOSED:
                    this.disconnect()
                    break
                default:
                    this.disconnect()
            }
        }

        this.disconnecting = false

        const endpoint = Amplify.getConfig().API?.REST?.['ocWebsocket']?.endpoint
        const token = await getAuthenticationInstance().getIdToken()

        if (!endpoint) {
            throw new Error("Couldn't find OcWebsocket endpoint")
        }

        this.ws = new WebSocket(`${endpoint}?authorization=${token}`)
        const connected = new Promise<void>((resolve, reject) => {
            this.resolveConnected = resolve
            setTimeout(() => reject(messages.getError('failedToConnectToWebsocket')), 10_000)
        })
        this.connectPromise = connected.finally(() => {
            if (this.connectPromise === connected) {
                this.connectPromise = undefined
            }
        })

        switch (this.ws.readyState) {
            case WebSocket.CONNECTING:
                this.ws.addEventListener('open', this.onOpen, {once: true})
                break
            case WebSocket.OPEN:
                this.onOpen()
                break
            default:
                this.disconnect()
                throw messages.getError('failedToConnectToWebsocket')
        }
        this.ws.addEventListener('message', this.onMessage)
        this.ws.addEventListener('close', this.onClose)

        await connected

        this.pingIntervalId = setInterval(() => this.ws?.send('ping'), 60_000)

        if (this.disconnecting) {
            this.disconnect()
        }
    }

    private async onOpen() {
        this.resolveConnected?.()
        try {
            await runWhile(() => this.ws?.readyState !== WebSocket.OPEN, 10, 100)

            this.ws?.send(
                JSON.stringify({
                    action: 'subscribe',
                    resource: this.resource,
                    customer: this.customer,
                    token: await getAuthenticationInstance().getIdToken()
                })
            )
        } catch (err: any) {
            throw messages.getError('failedToSubscribeToWebsocket')
        }
    }

    private onMessage(message: MessageEvent<any>) {
        this.onMessageCallback(JSON.parse(message.data.toString()))
    }

    private async onClose() {
        if (this.disconnecting) {
            return
        }

        this.disconnect()
        try {
            await this.connect()
        } catch (err) {
            snackHandler.error(messages.getText('errors', 'failedToConnectToWebsocket'))
            console.error(
                `OcWebsocket failed to reconnect to resource "${this.resource}" and customer "${this.customer}"`
            )
        }
    }

    public disconnect() {
        this.disconnecting = true
        if (this.pingIntervalId) {
            clearInterval(this.pingIntervalId)
            this.pingIntervalId = undefined
        }
        if (this.ws) {
            this.ws.removeEventListener('open', this.onOpen)
            this.ws.removeEventListener('message', this.onMessage)
            this.ws.removeEventListener('close', this.onClose)
            this.ws.close()
            this.ws = undefined
        }
    }
}
